import { DateTime } from "luxon";
import { useCallback, useMemo, useState } from "react";
import { resolveLoadedDay } from "../components/AddNurse/addStaffUtils";
import { FEATURE_FLAGS } from "../contexts/FeatureFlagsProvider";
import { useHospitalData } from "../contexts/HospitalDataProvider";
import { getStaffingDate, getWeekStart } from "../utils/dateUtils";
import { isNurseActiveInDepartment } from "../utils/formatters";
import { type AutoApproveRule, DEFAULT_WEEK_START_DAY, type Nurse, type RequestOutreach, type ShiftRequest, type WeekData } from "../utils/models";
import { dayIndexOfDate, panelWeeksOf, weekIndexOfDate } from "../utils/panelWeeks";
import { DIFF_HOURS_WARNING } from "../utils/warningDialogs/utils";
import {
	adjustPlan,
	callNow,
	type DemoState,
	pause,
	readOutreach,
	resetPlan,
	resume,
	skip,
	textAnyway,
	textNow,
	undoSkip,
} from "./demoOutreach/demoOutreachPlan";
import { splitRoster, WORK_HISTORY_DAYS } from "./demoOutreach/demoOutreachRoster";
import type { DemoContext } from "./demoOutreach/demoOutreachSeed";
import { initialStateOf } from "./demoOutreach/demoOutreachState";
import { useLastNameFirst } from "./useNameFormat";
import { useNowEveryMinute } from "./useNowEveryMinute";
import { useFeatureFlag } from "./useOpenFeatureFlag";
import type { RequestOutreachActions } from "./useRequestOutreach";
import { useRosterLookup } from "./useRosterLookup";
import { useScheduleDays } from "./useScheduleDays";
import { useScheduleCacheEnabled } from "./useScheduleWeekRules";

/**
 * DEMO ONLY (APP-933). A request's outreach built from the real request and roster, seeded from the request id, with
 * outreach controls on local state: nothing here makes a network call. Pickup review is handled separately by the
 * real scheduling workflow. APP-918 deletes this file and `demoOutreach/` and uses backend outreach data.
 */

/**
 * Session-wide, so closing and reopening the popup keeps what the user did; a reload starts over from the seed.
 */
const STORE = new Map<string, DemoState>();

const DAYS_THROUGH_NEXT_WEEK = 13;

type StoreEntry = { key: string; state: DemoState };

type SeedInputs = Pick<ReturnType<typeof useHospitalData>, "chosenDepartment" | "timezone"> & {
	nurses: Nurse[];
	/** The weeks around the request's staffing day, back through its work history. */
	weeks: WeekData[];
	staffingDate: DateTime;
	nameOf: (nurseId: number) => string;
	jobManagementEnabled: boolean;
	autoApprove: boolean;
	autoApproveRules: readonly AutoApproveRule[];
	now: DateTime;
};

// Active includes On Leave and Suspended: whether they can work this shift is the roster split's call.
const rosterOf = ({ nurses, chosenDepartment }: SeedInputs) =>
	nurses.filter((nurse) => isNurseActiveInDepartment(nurse, chosenDepartment.department_id)).sort((a, b) => a.id - b.id);

/**
 * Keyed on what the seed reads, so a new response, other times, roles or headcount, a roster or department setting
 * change seeds afresh rather than show a stale copy (the user's local actions on the old copy go with it). The loaded
 * weeks are left out on purpose: they refetch with every schedule change, which would wipe the demo mid-click.
 */
const storeKeyOf = (request: ShiftRequest, inputs: SeedInputs): string => {
	const { message } = request;
	const { chosenDepartment: department } = inputs;
	const responses = message.request_data.map(({ nurse_id: nurseId, status }) => `${nurseId}:${status}`).sort();
	return JSON.stringify([
		request.id,
		message.shift_start.toMillis(),
		message.shift_end.toMillis(),
		[...message.roles].sort(),
		request.job_id,
		request.charge_only,
		// A replace request has no headcount of its own (it replaces one person), so it keys on none.
		"needed_nurses" in request ? request.needed_nurses : null,
		responses,
		rosterOf(inputs).map((nurse) => nurse.id),
		inputs.timezone,
		department.dayStart,
		department.scheduled_shift_delta_minutes,
		department.longShiftDuration,
		inputs.jobManagementEnabled,
		inputs.autoApprove,
		inputs.autoApproveRules,
	]);
};

const seedStateOf = (request: ShiftRequest, inputs: SeedInputs): DemoState => {
	const { chosenDepartment, timezone, weeks, staffingDate, nameOf } = inputs;
	const departmentId = chosenDepartment.department_id;
	const roster = rosterOf(inputs);
	const graceMinutes = chosenDepartment.scheduled_shift_delta_minutes;
	const split = splitRoster({
		request,
		roster,
		departmentId,
		dayStart: chosenDepartment.dayStart,
		graceMinutes,
		jobManagementEnabled: inputs.jobManagementEnabled,
		// DEFAULT: the same long-day limit the daily-hours warning dialog falls back to (useNurseWarnings).
		longShiftHours: chosenDepartment.longShiftDuration ?? DIFF_HOURS_WARNING,
		weeks,
		loadedDay: resolveLoadedDay(staffingDate, {
			getWeekIndexByDate: (day) => weekIndexOfDate(weeks, day),
			getDayIndexByDate: (day) => dayIndexOfDate(weeks, day),
		}),
		nameOf,
	});
	const ctx: DemoContext = {
		nurses: inputs.nurses,
		nameOf,
		timezone,
		now: inputs.now,
		autoApprove: inputs.autoApprove,
		autoApproveRules: inputs.autoApproveRules,
	};
	return initialStateOf(request, ctx, split);
};

export type DemoRequestOutreach = { outreach: RequestOutreach; actions: RequestOutreachActions };

/**
 * The demo outreach for `request`: `null` when `enabled` is false (so a real department never builds the roster) and
 * until the roster and the request's weeks are in; `isLoading` while they load, since seeding without them would keep
 * a plan with no shift checks for the rest of the session, and `isError` with `retry` when one failed.
 */
export const useDemoRequestOutreach = (
	request: ShiftRequest,
	enabled: boolean,
): { demo: DemoRequestOutreach | null; isLoading: boolean; isError: boolean; retry: () => void } => {
	const isCacheEnabled = useScheduleCacheEnabled();
	const { hospitalData, chosenDepartment, timezone } = useHospitalData();
	const lastNameFirst = useLastNameFirst();
	const roster = useRosterLookup(chosenDepartment.department_id);
	const { nurses, getNurseById, getNurseFormattedName } = roster;
	const shiftStartMillis = request.message.shift_start.toMillis();
	const staffingDate = useMemo(
		() => getStaffingDate(DateTime.fromMillis(shiftStartMillis, { zone: timezone }), chosenDepartment.dayStart, chosenDepartment.scheduled_shift_delta_minutes),
		[shiftStartMillis, timezone, chosenDepartment.dayStart, chosenDepartment.scheduled_shift_delta_minutes],
	);
	// With the cache on, from the Sunday week holding the start of the "worked these hours" history through the week after
	// the shift's (the shifts around its day can cross into it). With it off nothing is asked: the old list, as before.
	const historyDays = useScheduleDays(
		getWeekStart(staffingDate.minus({ days: WORK_HISTORY_DAYS }), DEFAULT_WEEK_START_DAY),
		getWeekStart(staffingDate, DEFAULT_WEEK_START_DAY).plus({ days: DAYS_THROUGH_NEXT_WEEK }),
	);
	const weeks = useMemo(
		() => (isCacheEnabled ? panelWeeksOf(historyDays.days, getNurseById, lastNameFirst) : hospitalData.weeks),
		[isCacheEnabled, historyDays.days, getNurseById, lastNameFirst, hospitalData.weeks],
	);
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	// DEFAULT: `autoApproveRules` is optional on the department; absent means none are configured.
	const autoApproveRules = chosenDepartment.autoApproveRules ?? [];
	const autoApprove = useFeatureFlag(FEATURE_FLAGS.AutoApproveShifts) && autoApproveRules.length > 0;
	// One clock for what's derived and what's done: the view and every action read the same minute.
	const minute = useNowEveryMinute();
	const now = useMemo(() => minute.setZone(timezone), [minute, timezone]);
	const isLoading = enabled && (roster.isLoading || historyDays.isLoading);
	const isError = enabled && !isLoading && (roster.isError || historyDays.isError);
	const canSeed = enabled && !isLoading && !isError && roster.roster !== undefined;
	const inputs: SeedInputs = {
		chosenDepartment,
		timezone,
		nurses,
		weeks,
		staffingDate,
		nameOf: getNurseFormattedName,
		jobManagementEnabled,
		autoApprove,
		autoApproveRules,
		now,
	};
	const key = storeKeyOf(request, inputs);

	const loadOrSeed = (): DemoState => {
		const stored = STORE.get(key);
		if (stored !== undefined) return stored;
		const created = seedStateOf(request, inputs);
		STORE.set(key, created);
		return created;
	};

	const [entry, setEntry] = useState<StoreEntry | null>(() => (canSeed ? { key, state: loadOrSeed() } : null));
	// The real inputs changed under the open popup, or just loaded: switch to their own entry, seeding it if it's new.
	if (canSeed && entry?.key !== key) setEntry({ key, state: loadOrSeed() });

	const update = useCallback(
		(change: (current: DemoState, at: DateTime) => DemoState) =>
			setEntry((current) => {
				if (current === null) throw new Error("Demo outreach: an action ran on a department without demo outreach");
				const next = change(current.state, now);
				STORE.set(current.key, next);
				return { key: current.key, state: next };
			}),
		[now],
	);

	const actions = useMemo<RequestOutreachActions>(
		() => ({
			pause: () => update((current) => pause(current)),
			resume: () => update((current, at) => resume(current, at)),
			textNow: (nurseId) => update((current, at) => textNow(current, nurseId, at)),
			callNow: (nurseId) => update((current, at) => callNow(current, nurseId, at)),
			skip: (nurseId) => update((current, at) => skip(current, nurseId, at)),
			undoSkip: (nurseId) => update((current, at) => undoSkip(current, nurseId, at)),
			textAnyway: (nurseId) => update((current, at) => textAnyway(current, nurseId, at)),
			adjustPlan: (choice) => update((current, at) => adjustPlan(current, choice, at)),
			resetPlan: () => update((current, at) => resetPlan(current, at)),
		}),
		[update],
	);

	const state = canSeed && entry !== null && entry.key === key ? entry.state : null;
	const outreach = useMemo(() => (state === null ? null : readOutreach(state, now)), [state, now]);
	const retry = () => {
		if (roster.isError) roster.refetch();
		if (historyDays.isError) historyDays.refetch();
	};
	return { demo: outreach === null ? null : { outreach, actions }, isLoading, isError, retry };
};
